/**
 * PreconditionsPanel
 *
 * Renders the polymorphic ``preconditions`` list declared on a
 * :class:`WorkflowAction`. Each precondition kind has its own
 * renderer; results are collected into a flat ``Record<string,
 * unknown>`` keyed by ``precondition.key`` and posted back to the
 * engine in ``captured_values.preconditions`` (see
 * ``WorkflowActionDialog``).
 *
 * Adding a new kind
 * -----------------
 *
 * 1. Extend :class:`PreconditionSpec.kind` with the new discriminator.
 * 2. Teach the backend ``precondition_registry`` how to validate it.
 * 3. Add a switch arm here that returns a renderer reading from
 *    ``values[spec.key]`` and writing back via ``onChange``.
 *
 * The renderer is intentionally dumb about whether a precondition is
 * "satisfied" - that decision lives in
 * ``WorkflowActionDialog.isPreconditionSatisfied`` so the same logic
 * can drive the submit-button enabled state.
 */

import { useCallback } from 'react'
import { AlertCircle, BookOpen, CheckCircle2, FileUp } from 'lucide-react'

import { Checkbox } from '@/shared/components/ui/checkbox'
import { Label } from '@/shared/components/ui/label'
import type { PreconditionSpec } from '@/shared/lib/workflows/types'

interface PreconditionsPanelProps {
  specs: PreconditionSpec[]
  values: Record<string, unknown>
  onChange: (next: Record<string, unknown>) => void
}

export function PreconditionsPanel({
  specs,
  values,
  onChange,
}: PreconditionsPanelProps) {
  const setValue = useCallback(
    (key: string, next: unknown) => {
      onChange({ ...values, [key]: next })
    },
    [values, onChange],
  )

  return (
    <div className="flex flex-col gap-2 rounded-md border border-[var(--rule)] bg-[var(--bg-subtle)] p-3">
      {specs.map((spec) => (
        <PreconditionItem
          key={spec.key}
          spec={spec}
          value={values[spec.key]}
          onValueChange={(next) => setValue(spec.key, next)}
        />
      ))}
    </div>
  )
}

interface PreconditionItemProps {
  spec: PreconditionSpec
  value: unknown
  onValueChange: (next: unknown) => void
}

function PreconditionItem({ spec, value, onValueChange }: PreconditionItemProps) {
  switch (spec.kind) {
    case 'acknowledgement':
      return (
        <AcknowledgementPrecondition
          spec={spec}
          value={value === true}
          onValueChange={onValueChange}
        />
      )
    case 'checklist':
      return (
        <ChecklistPrecondition
          spec={spec}
          value={(value as Record<string, boolean>) ?? {}}
          onValueChange={onValueChange}
        />
      )
    case 'briefing':
      return <BriefingPrecondition spec={spec} />
    case 'attachment':
      return (
        <AttachmentPrecondition
          spec={spec}
          value={(value as File[]) ?? []}
          onValueChange={onValueChange}
        />
      )
    default:
      // Forward-compatibility fallback: render the raw payload so an
      // unknown ``kind`` is at least visible to the user instead of
      // silently disappearing.
      return (
        <div className="flex items-start gap-2 text-xs text-[var(--ink-3)]">
          <AlertCircle className="mt-0.5 h-3 w-3" />
          <span>
            Unsupported precondition kind <code>{spec.kind}</code> - please
            update your client.
          </span>
        </div>
      )
  }
}

// ─── Acknowledgement ──────────────────────────────────────

function AcknowledgementPrecondition({
  spec,
  value,
  onValueChange,
}: {
  spec: PreconditionSpec
  value: boolean
  onValueChange: (next: boolean) => void
}) {
  const text =
    (spec.display_payload?.text as string | undefined) ??
    'I acknowledge the requirement above.'
  return (
    <label className="flex cursor-pointer items-start gap-2 text-sm text-[var(--ink)]">
      <Checkbox
        checked={value}
        onCheckedChange={(c) => onValueChange(c === true)}
        className="mt-0.5"
      />
      <span className="leading-snug">{text}</span>
    </label>
  )
}

// ─── Checklist ────────────────────────────────────────────

interface ChecklistItem {
  key: string
  label: string
}

function ChecklistPrecondition({
  spec,
  value,
  onValueChange,
}: {
  spec: PreconditionSpec
  value: Record<string, boolean>
  onValueChange: (next: Record<string, boolean>) => void
}) {
  const title =
    (spec.display_payload?.title as string | undefined) ??
    'Confirm the following'
  const items =
    (spec.display_payload?.items as ChecklistItem[] | undefined) ?? []
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-[11px] uppercase tracking-[0.08em] text-[var(--ink-3)]">
        {title}
      </Label>
      <ul className="flex flex-col gap-1">
        {items.map((item) => {
          const checked = value[item.key] === true
          return (
            <li key={item.key}>
              <label className="flex cursor-pointer items-start gap-2 text-sm text-[var(--ink)]">
                <Checkbox
                  checked={checked}
                  onCheckedChange={(c) =>
                    onValueChange({ ...value, [item.key]: c === true })
                  }
                  className="mt-0.5"
                />
                <span className="leading-snug">
                  {checked ? (
                    <CheckCircle2 className="mr-1 inline h-3 w-3 text-[var(--green)]" />
                  ) : null}
                  {item.label}
                </span>
              </label>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

// ─── Briefing (read-only doc) ─────────────────────────────

function BriefingPrecondition({ spec }: { spec: PreconditionSpec }) {
  const title = (spec.display_payload?.title as string | undefined) ?? 'Briefing'
  const body = (spec.display_payload?.body as string | undefined) ?? ''
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.08em] text-[var(--ink-3)]">
        <BookOpen className="h-3 w-3" />
        {title}
      </div>
      <div className="whitespace-pre-wrap rounded-[6px] border border-[var(--rule)] bg-[var(--bg)] p-2.5 text-[12.5px] leading-snug text-[var(--ink-2)]">
        {body}
      </div>
    </div>
  )
}

// ─── Attachment ───────────────────────────────────────────

function AttachmentPrecondition({
  spec,
  value,
  onValueChange,
}: {
  spec: PreconditionSpec
  value: File[]
  onValueChange: (next: File[]) => void
}) {
  const title =
    (spec.display_payload?.title as string | undefined) ?? 'Attach a file'
  const accept = (spec.display_payload?.accept as string | undefined) ?? '*/*'
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.08em] text-[var(--ink-3)]">
        <FileUp className="h-3 w-3" />
        {title}
      </Label>
      <input
        type="file"
        accept={accept}
        multiple
        onChange={(e) => {
          const files = Array.from(e.target.files ?? [])
          onValueChange(files)
        }}
        className="text-xs"
      />
      {value.length > 0 ? (
        <p className="text-[11px] text-[var(--ink-3)]">
          {value.length} file{value.length === 1 ? '' : 's'} selected
        </p>
      ) : null}
    </div>
  )
}
